import classNames from 'classnames';
import Customer from 'models/customer';
import LayoutInput from 'components/forms/layoutInput';
import moment from 'moment';
import path from 'path';
import PropTypes from 'prop-types';
import React from 'react';
import session from 'services/sessionService';
import {Button, Icon} from 'components/layout';
import {canMerge} from 'helpers/systemMerge';
import {chemicalBaseTypeNames, truBlueStatus} from 'helpers/constants';
import {FormattedMessage, intlShape, injectIntl} from 'react-intl';
import {Row, Col} from 'components';
import {systemOptionsTags} from 'helpers/dataHelpers';

// Row object
class SystemWidget extends React.Component {
  static defaultProps = {
    customer: {
      customerContacts: [],
    },
    pools: [],
    edit: false,
  };

  static propTypes = {
    dealer: PropTypes.object.isRequired,
    customer: PropTypes.instanceOf(Customer).isRequired,
    pools: PropTypes.array.isRequired,
    contact: PropTypes.object,
    edit: PropTypes.bool,
    onChange: PropTypes.func,
    intl: intlShape.isRequired,
  };

  static getPastDueWarning(pool) {
    //Generates custom warning text for past due water analysis for a specific pool. (Deals with leap years by splitting diff cases)
    let today = moment();
    let nextAnalysisDate = pool.nextAnalysisDate;

    if (!nextAnalysisDate) {
      return null;
    }

    let yearsOver = today.diff(moment(nextAnalysisDate), 'years');
    let monthsOver = today.diff(moment(nextAnalysisDate), 'months');
    let daysOver = today.diff(moment(nextAnalysisDate), 'days');

    if (yearsOver >= 1) {
      return `Over ${yearsOver} year(s)`;
    }
    if (monthsOver >= 1) {
      return `Over ${monthsOver} month(s)`;
    }
    if (daysOver >= 1) {
      return `${daysOver} day(s)`;
    }

    return null;
  }

  runAnalysis = (poolID, customerUrl) => {
    if (session.getUser().privileges.canWaterTest) {
      return e => {
        e.stopPropagation();
        window.location.hash = path.join('#customer', customerUrl, poolID.toString(), 'analysis');
      };
    }
    return () => {
    };
  };

  updatePoolField = (index, field) => {
    return event => {
      if (this.props.onChange) {
        this.props.onChange(index, field, event.value);
      }
    };
  };

  viewPool = (poolID, customerUrl) => {
    if (!this.props.edit) {
      window.location.hash = path.join('#customer', customerUrl, poolID.toString(), 'details');
    }
  };

  render() {
    let {formatMessage} = this.props.intl;
    //Generate customer id for url purpose
    let customerUrl = this.props.customer.id.toString();

    let maintenanceProgram = this.props.dealer && this.props.dealer.nameMaintenanceProgram ?
      this.props.dealer.nameMaintenanceProgram :
      formatMessage({id: 'preferences.scheduled.maintenance'});

    if (this.props.contact && !isNaN(this.props.contact.id)) {
      customerUrl += '.' + this.props.contact.id;
    }

    let disabled = !session.getUser().privileges.canWaterTest;
    let iconStyle = {marginLeft: 5, marginRight: 5, marginTop: 1, color: '#fff'};
    let pools = this.props.pools
      .sort((a, b) => a.id < b.id ? -1 : 1)
      .map((pool, index) => {
        let poolLocation = systemOptionsTags[pool.typeID].location;
        let poolFinish = systemOptionsTags[pool.typeID].finish;
        let flagged = pool.flag ? <i className='bl bl-flag-filled pull-right'
                                   style={iconStyle}/> : null;
        let sourceConcern = this.props.customer && this.props.customer.sourceConcern ?
          <i className='bl bl-water-pipe pull-right'
           style={iconStyle}/> : null;
        let prevAnalysisDate = pool.prevAnalysisDate ? moment(pool.prevAnalysisDate).format('ll') : formatMessage({id: 'general.none'});

        const isCanada = this.props.dealer && this.props.dealer.country && this.props.dealer.country.toLowerCase() === 'ca';
        let volume = pool.volume.format(0, 3) + ' ' + formatMessage({id: isCanada ? 'quick-calcs.volume.litres' : 'quick-calcs.volume.gals'});

        let truBlue = this.props.dealer.isTruBlueEligible && pool.truBlueStatus !== truBlueStatus.Ineligible ?
          <i className={classNames('bl', 'icon', 'pull-right', 'fa-lg', 'bl-tru-blue-filled', 'bl-tru-blue-circle-icon', {
            'blue': pool.truBlueStatus === truBlueStatus.Active,
            'grey': pool.truBlueStatus === truBlueStatus.Inactive ||
          pool.truBlueStatus === truBlueStatus.Eligible ||
          pool.truBlueStatus === truBlueStatus.Opportunity,
          })}/> : null;

        let pastDueWarning = SystemWidget.getPastDueWarning(pool);

        let isInMaintenance = pool.isInMaintenance ?
          <p className='medium-font'>
            <i className={'icon bl bl-truck-filled'}/>
            {maintenanceProgram}
          </p> :
        null;

        const none = `(${formatMessage({id: 'general.none'})})`;

        return <div key={pool.id}>
          <div className={`pool pool-listing ${pool.nickName || this.props.edit || pastDueWarning ?
          (pool.nickName || this.props.edit) && pastDueWarning ? 'pool-listing--xlarge' : 'pool-listing--large' : ''}`}
             onClick={this.viewPool.bind(this, pool.id, customerUrl)}>
            <div className={`customer-pool-header ${pool.truBlueStatus === truBlueStatus.Active && this.props.dealer.isTruBlueEligible ?
            'customer-pool-header--active' : ''}`}>
              {pool.softSoakTRIO ? 'Trio ' : null}
              {chemicalBaseTypeNames(pool.typeID, pool.sanitizerType || 2, formatMessage)}
              {sourceConcern}
              {flagged}
              {truBlue}
            </div>
            <div className='pool-details'>
              {pool.nickName || this.props.edit ?
                <p>
                  <LayoutInput
                  editMode={this.props.edit}
                  placeholder={formatMessage({id: 'system.widget.add.nickname'})}
                  noLabel
                  dataKey='nickName'
                  value={pool.nickName}
                  dataChanged={this.updatePoolField(index, 'nickName')}
                  maxLength='50'/>
                </p> :
              null}
              {isInMaintenance}
              <p>
                {poolLocation[pool.location] ? formatMessage({id: poolLocation[pool.location]}) : none}
                <span className='separator'> | </span>
                {poolFinish[pool.finish] ? formatMessage({id: poolFinish[pool.finish]}) : none}
                <span className='separator'> | </span>
                {volume}.
              </p>
              <p>
                <FormattedMessage id="system.widget.lifetime.analyses"/>
                <span className='detail--highlighted b'>{pool.scanCount}</span></p>
              <p>
                <FormattedMessage id="general.last.analysis"/>: <span className='detail--highlighted b'>{prevAnalysisDate}</span>
              </p>
            </div>
            <button type='button'
                  className='btn btn-block water-analysis-button center-block'
                  data-event-category='User'
                  data-event-action='customer-run-analysis'
                  disabled={disabled}
                  onClick={this.runAnalysis(pool.id, customerUrl)}>
              <i className='bl bl-test-tube'
               style={{position: 'relative'}}/>
              <span style={{marginLeft: 10}}><FormattedMessage id="system.widget.start.analysis"/></span>
            </button>
          </div>
        </div>;
      });

    return <div>
      <Row>
        <Col md={12}>
          <h2 style={{marginBottom: 10}}><FormattedMessage id="system.widget.title"/></h2>
        </Col>
        <Col md={12}>
          {canMerge(this.props.pools) ?
            <Button link
                    style={{textAlign: 'left', marginBottom: 5}}
                    href={path.join('#customer', customerUrl, 'merge-systems', '0')}><Icon name='merge'/>
              <FormattedMessage id="system.widget.merge.systems"/></Button> :
            null}
        </Col>
        <Col md={12}>
          <Button link
                  href={path.join('#customer', customerUrl, 'add-pool', 'details')}>+ <FormattedMessage id="system.widget.add.system"/></Button>
        </Col>
      </Row>
      {pools}
    </div>;
  }
}

export default injectIntl(SystemWidget);
